import { useQuery } from 'urql';
import { TitleText } from '~/components/ui/BasicListItem';
import { CardSmallStatic } from '~/components/ui/CardSmall';
import { Image } from '~/components/ui/Image';
import { ListAssetsQuery } from '~/generated/graphql';
import { graphql } from '~/gql';
import type { AssetDeploymentsQuery } from '~/gql/graphql';
import { LONG_FORMAT, formatDate } from '~/helpers/formatDate';

type Deployment = NonNullable<AssetDeploymentsQuery['asset']>['deployments'][0];

// Use the actual deployment type from ListAssetsQuery instead of manually retyping
type CurrentDeployment = NonNullable<
  ListAssetsQuery['assets'][number]['deployment']
>;

export const AssetDeploymentsDocument = graphql(`
  query AssetDeployments($id: ID!, $jobId: ID, $taskId: ID) {
    asset(id: $id) {
      id
      deployments(jobId: $jobId, taskId: $taskId) {
        id
        status
        startedAt
        image
        displayLocation
      }
    }
  }
`);

export function useAssetDeployments(
  assetId: string,
  enabled = true,
  jobId?: string,
  taskId?: string
) {
  const [result, reexecuteQuery] = useQuery({
    query: AssetDeploymentsDocument,
    variables: {
      id: assetId,
      jobId: jobId || undefined,
      taskId: taskId || undefined,
    },
    requestPolicy: 'cache-first',
    pause: !assetId || !enabled,
  });

  const assetDeployments = result.data?.asset?.deployments ?? [];
  return [
    assetDeployments,
    () => reexecuteQuery({ requestPolicy: 'network-only' }),
    result.fetching,
    result.error,
  ] as [typeof assetDeployments, () => void, boolean, typeof result.error];
}

export function AssetDeployments({
  assetId,
  currentDeployment,
  preloadedDeployments,
  jobId,
}: {
  assetId: string;
  currentDeployment?: CurrentDeployment | null;
  preloadedDeployments?: ReturnType<typeof useAssetDeployments>[0];
  jobId?: string;
}) {
  // Use preloaded deployments if available, otherwise fall back to direct query
  const [directDeployments] = useAssetDeployments(
    assetId,
    !preloadedDeployments, // Only query directly if no preloaded data
    jobId
  );

  const deployments = preloadedDeployments || directDeployments;
  const deployment = currentDeployment;

  if (!deployment && !deployments.length) {
    return <div className='p-4'>This asset has no deployments</div>;
  }

  return (
    <>
      {/* Current Deployment Information */}
      {deployment && (
        <div className='border-1 mx-4 mb-5 mt-6 space-y-4 rounded-lg border border-outline-variant bg-white p-6 shadow-[2px_2px_3px_0px_rgba(0,0,0,0.1)]'>
          <h3 className='text-md font-medium text-gray-900'>
            Current Deployment
          </h3>
          <div
            className={
              'mb-2 flex items-center gap-2 rounded border border-grey-20 bg-surface-bright px-2 py-2 lg:mb-3 lg:px-4 lg:py-3'
            }
          >
            <div className=' h-9 w-9 xs:block lg:h-14 lg:w-14'>
              <Image
                url={deployment.image}
                size='fit'
                onImageError={() =>
                  console.error('Image failed to load:', deployment.image)
                }
                onImageLoad={() => {
                  if (process.env.NODE_ENV === 'development') {
                    console.log('Image loaded successfully:', deployment.image);
                  }
                }}
              />
            </div>

            <div className='ml-2 mr-3 flex-1 overflow-hidden'>
              <div className='items-center gap-2 md:flex '>
                <div className='mb-0 flex-shrink flex-grow-0 text-sm font-semibold leading-4 text-grey-70 md:text-base lg:max-w-[70%] xl:mb-0'>
                  <TitleText text={deployment.displayLocation} />
                </div>
                <div className='secondary-text mb-0 flex-1 flex-grow truncate text-sm text-grey-40 md:mt-0 md:text-base'>
                  {deployment.space.name}
                </div>
              </div>
              <div className='tertiary-text hidden truncate text-xs text-grey-40 md:block'>
                {formatDate(deployment.startedAt, LONG_FORMAT)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deployment History */}
      {deployments.length > 0 && (
        <>
          <p className='mb-3 block px-3 text-sm font-medium text-grey-50'>
            Deployment History
          </p>
          {deployments.map((deployment: Deployment) => (
            <CardSmallStatic
              key={deployment.id}
              titleText={deployment.displayLocation}
              secondaryText={formatDate(deployment.startedAt, LONG_FORMAT)}
              image={deployment.image}
              status={deployment.status}
            />
          ))}
        </>
      )}
    </>
  );
}
